import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DsyListElement } from '../../../../models/dsy-list-element.model';
import { Organization, Site } from '../../../../models/organization.model';
import { PortfolioFiltersStore } from '../../../../store/signal-stores/portfolio-filters.signal-store';
import { haveSameIds } from '../../../shared/helpers/array-helpers';
import { SearchableDropdownComponent } from '../../../shared/components/searchable-dropdown/searchable-dropdown.component';

@Component({
  selector: 'fdr-past-performance-ui-dispatch-portfolio-filters',
  imports: [TranslateModule,SearchableDropdownComponent],
  templateUrl: './dispatch-portfolio-filters.component.html',
  styleUrl: './dispatch-portfolio-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [PortfolioFiltersStore],
})
export class DispatchPortfolioFiltersComponent {
  readonly store = inject(PortfolioFiltersStore);

  readonly organizations = input.required<Organization[]>();
  readonly sites = input.required<Site[]>();
  readonly dropdownListConfig = { maxHeight: '300px' };
  readonly appliedFiltersChange = output<{ organizationNames: string[]; siteNames: string[] }>();

  readonly organizationFilterEntriesSignal = computed<DsyListElement[]>(() =>
    this.organizations().map(org => ({
      id: org.id,
      labelContent: org.name,
      isSelected: this.store.isOrganizationSelected()(org.id),
      onListElementClick: () => this.store.toggleOrganization(org),
      hasRightCheckbox: true,
      rightCheckboxSize: 'small',
    }))
  );

  readonly siteFilterEntriesSignal = computed<DsyListElement[]>(() => {
    const selectedOrgIds = new Set(this.store.selectedOrganizations().map(org => org.id));
    const visibleSites = selectedOrgIds.size > 0
      ? this.sites().filter(site => selectedOrgIds.has(site.organizationId))
      : this.sites();

    return visibleSites.map(site => ({
      id: site.id,
      labelContent: site.name,
      isSelected: this.store.isSiteSelected()(site.id),
      onListElementClick: () => this.store.toggleSite(site),
      hasRightCheckbox: true,
      rightCheckboxSize: 'small',
    }));
  });

  readonly resetButtonDisabledSignal = computed(() => {
    const hasSelectedOrgs = this.store.selectedOrganizations().length > 0;
    const hasSelectedSites = this.store.selectedSites().length > 0;
    return !hasSelectedOrgs && !hasSelectedSites;
  });

  readonly applyButtonDisabledSignal = computed(() => {
    const selectedOrgs = this.store.selectedOrganizations();
    const appliedOrgs = this.store.appliedOrganizations();

    const selectedSites = this.store.selectedSites();
    const appliedSites = this.store.appliedSites();

    const isSameOrgs = haveSameIds(selectedOrgs, appliedOrgs);
    const isSameSites = haveSameIds(selectedSites, appliedSites);

    return isSameOrgs && isSameSites;
  });

  onReset() {
    this.store.reset();
    this.appliedFiltersChange.emit({ organizationNames: [], siteNames: [] });
  }

  onApply() {
    this.store.apply();
    this.appliedFiltersChange.emit({
      organizationNames: this.store.appliedOrganizations().map(o => o.name),
      siteNames: this.store.appliedSites().map(s => s.name),
    });
  }
}
